// 牌型识别与比较（含逢人配）。纯函数，Node 直接跑测试。
// 牌型（竞赛规则第三条）：单张、对子、三同张、三带对、顺子（正好 5 张）、三连对、三同连张（三同连张）、炸弹（4–10 张）、同花顺、四大天王。
// 大小（第四条）：四大天王 > 超过五张的炸弹（张数多者大）> 同花顺 > 五张炸弹 > 四张炸弹 > 其他 7 种牌型；同档比点数，不比花色。
import { BIG_JOKER, isJoker, isWild, power, SMALL_JOKER } from './cards.ts';
import type { Card } from './cards.ts';

export type PlayType =
  | 'single' | 'pair' | 'triple' | 'fullHouse' | 'straight' | 'tube' | 'plate'
  | 'bomb' | 'straightFlush' | 'jokerBomb';

export interface Play {
  type: PlayType;
  /** 同牌型比较用的键：单/对/三/三带对/炸弹用 power；连牌类用最高张的自然点数（A2345 → 5）。 */
  key: number;
  size: number;
  cards: Card[];
}

export const PLAY_NAME: Record<PlayType, string> = {
  // 名称按竞赛规则第三条
  single: '单张', pair: '对子', triple: '三同张', fullHouse: '三带对', straight: '顺子', tube: '三连对',
  plate: '三同连张', bomb: '炸弹', straightFlush: '同花顺', jokerBomb: '四大天王',
};

export const isBombType = (t: PlayType): boolean => t === 'bomb' || t === 'straightFlush' || t === 'jokerBomb';

/** 炸弹档位：4 张=1，5 张=2，同花顺=3，6 张=4 … 10 张=8，四大天王=9。 */
export function bombTier(p: Play): number {
  if (p.type === 'jokerBomb') return 9;
  if (p.type === 'straightFlush') return 3;
  if (p.type === 'bomb') return p.size <= 5 ? p.size - 3 : p.size - 2;
  return 0;
}

/** 连牌中的位置点数：1 表示当 1 用的 A，14 表示当最大用的 A。 */
const natural = (pos: number): number => (pos === 1 ? 14 : pos);

/**
 * 列出这手牌所有合法的解释（逢人配可能带来多种）。空数组 = 不成牌型。
 * 返回按“炸弹优先、键大优先”排序，第一项是默认解释。
 */
export function analyze(cards: Card[], level: number): Play[] {
  const n = cards.length;
  if (n === 0) return [];
  const wilds = cards.filter((c) => isWild(c, level)).length;
  const fixed = cards.filter((c) => !isWild(c, level));
  const jokers = fixed.filter(isJoker);
  const plain = fixed.filter((c) => !isJoker(c));
  const count = new Map<number, number>();
  for (const c of plain) count.set(c.rank, (count.get(c.rank) ?? 0) + 1);
  const ranks = [...count.keys()];
  const out: Play[] = [];
  const add = (type: PlayType, key: number) => out.push({ type, key, size: n, cards });

  // 四大天王
  if (n === 4 && jokers.length === 4) add('jokerBomb', BIG_JOKER);

  // 同点类：单、对、三、炸弹
  if (n === 1) add('single', isJoker(cards[0]) ? cards[0].rank : power(cards[0].rank, level));
  if (n === 2 && jokers.length === 2 && jokers[0].rank === jokers[1].rank) add('pair', jokers[0].rank);
  if (n >= 2 && jokers.length === 0 && ranks.length <= 1) {
    const r = ranks[0] ?? level; // 全是逢人配：按级牌本身
    if (n === 2) add('pair', power(r, level));
    if (n === 3) add('triple', power(r, level));
    if (n >= 4 && n <= 10) add('bomb', power(r, level));
  }

  // 三带对：三张不能是王；对子可以是一对同色王（王不能由逢人配补）
  if (n === 5) {
    const jokerPair = jokers.length === 2 && jokers[0].rank === jokers[1].rank ? jokers[0].rank : 0;
    if (jokers.length === 0 || jokerPair) {
      for (let t = 2; t <= 14; t++) {
        const pairs = jokerPair ? [jokerPair] : Array.from({ length: 13 }, (_, i) => i + 2).filter((p) => p !== t);
        for (const p of pairs) {
          if (!plain.every((c) => c.rank === t || c.rank === p)) continue;
          const needT = 3 - (count.get(t) ?? 0);
          const needP = p >= SMALL_JOKER ? 0 : 2 - (count.get(p) ?? 0);
          if (needT >= 0 && needP >= 0 && needT + needP === wilds) {
            if (!out.some((o) => o.type === 'fullHouse' && o.key === power(t, level))) add('fullHouse', power(t, level));
          }
        }
      }
    }
  }

  // 连牌：顺子 5×1、三连对 3×2、三同连张 2×3；A 可作 1 或 14
  const runs: Array<[PlayType, number, number]> = [['straight', 5, 1], ['tube', 3, 2], ['plate', 2, 3]];
  if (jokers.length === 0) {
    for (const [type, len, width] of runs) {
      if (n !== len * width) continue;
      for (let start = 1; start + len - 1 <= 14; start++) {
        const slots = Array.from({ length: len }, (_, i) => natural(start + i));
        if (!plain.every((c) => slots.includes(c.rank))) continue;
        let missing = 0;
        let fits = true;
        for (const r of slots) {
          const have = count.get(r) ?? 0;
          if (have > width) fits = false;
          missing += width - have;
        }
        if (!fits || missing !== wilds) continue;
        const top = start + len - 1; // A2345 的最高张是 5
        add(type, top);
        if (type === 'straight' && new Set(plain.map((c) => c.suit)).size <= 1) add('straightFlush', top);
      }
    }
  }

  return out.sort((a, b) => bombTier(b) - bombTier(a) || b.key - a.key);
}

/** a 能否压住 b。 */
export function beats(a: Play, b: Play): boolean {
  const ta = bombTier(a);
  const tb = bombTier(b);
  if (ta || tb) return ta > tb || (ta === tb && ta !== 9 && a.key > b.key);
  return a.type === b.type && a.size === b.size && a.key > b.key;
}

/**
 * 这手牌在当前局面下所有合法的出法（同牌型同键只留一个）。领出时按“炸弹优先、键大优先”；
 * 跟牌时只留压得住的，同牌型优先、炸弹其次（小炸在前）。第一项是默认出法。
 */
export function legalPlays(cards: Card[], level: number, target: Play | null): Play[] {
  const seen = new Set<string>();
  const options = analyze(cards, level).filter((p) => {
    const k = `${p.type}:${p.key}`;
    return !seen.has(k) && (seen.add(k), true);
  });
  if (!target) return options;
  const ok = options.filter((p) => beats(p, target));
  const normal = ok.filter((p) => !isBombType(p.type)).sort((a, b) => b.key - a.key);
  const bombs = ok.filter((p) => isBombType(p.type)).sort((a, b) => bombTier(a) - bombTier(b) || a.key - b.key);
  return [...normal, ...bombs];
}

/** 跟牌时挑一个能压住 target 的解释；同牌型优先，炸弹其次。没有返回 null。 */
export const interpretAgainst = (cards: Card[], level: number, target: Play | null): Play | null =>
  legalPlays(cards, level, target)[0] ?? null;
